/**
 * The run history table (mission 6 WP09 T050; FR-019, FR-023, FR-027,
 * NFR-005; D.5, ANA.9, GEN.6, U.2, TX.6; sheet 10 callouts 5–8; research
 * §1.20; style guide §2.6, §7.7, §7.17, §9; T13).
 *
 * Presentational, on WP03's `Table` parts. One row per run in the API's order
 * (newest first; nothing is sorted here). Counts, credits, ids, versions and
 * filenames are mono; the ERROR · WARN · INFO cells carry
 * `data-severity-count`, so the page must also render the ANA.4 legend (T13).
 * "Times in UTC" is stated once, in the caption.
 *
 * Rows are not focusable: the table opens no drawer, so each row's keyboard
 * target is its run link (§9). A mouse click elsewhere on the row navigates to
 * the same page; a click on a link is left to the link.
 *
 * The output column records downloads of the files themselves (a findings
 * export is not counted by the API) and, from mission 7 (WP11 T059; FR-016;
 * sheet 10 callout 7; TX.6, D.1), the run's latest transmission from
 * `latest_transmission`, linking to the run's transmission log; a null value
 * adds nothing. Paging state lives in RunHistory; this table renders one page of at
 * most 50 and the footer range, with no total (the API gives none).
 */
import { Fragment, type MouseEvent, type ReactNode } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'

import { Band } from '@/components/trellis/Band'
import { ProblemBand } from '@/components/trellis/ProblemBand'
import { runStateTone } from '@/components/trellis/RunHeader'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeletonRows,
} from '@/components/ui/table'
import { ApiError } from '@/lib/api'
import type { RunPath, RunSummary } from '@/lib/api/runs'
import { Mono, count, date, isoDateTime, shortId, time } from '@/lib/format'
import { PROVISIONAL_COPY, copyText } from '@/lib/provisional-copy'

export const PATH_WORDS: Record<RunPath, string> = {
  generate: 'Generate (CSV or XLSX)',
  analyze: 'Analyze Metro 2',
}

/** The §2.6 badge word: the state in sentence case; an unknown state renders as given. */
export function stateWord(state: string): string {
  const known = ['queued', 'validating', 'generating', 'analyzing', 'ready', 'rejected', 'failed', 'cancelled']
  return known.includes(state) ? state.charAt(0).toUpperCase() + state.slice(1) : state
}

const HEADERS = ['Run', 'Date', 'Type', 'Input file', 'Records', 'Rule set'] as const
const SEVERITIES = ['ERROR', 'WARN', 'INFO'] as const
const COLUMNS = HEADERS.length + SEVERITIES.length + 3

export interface HistoryPaging {
  /** Rows before this page, for the footer range. */
  offset: number
  hasPrevious: boolean
  hasNext: boolean
  onPrevious(): void
  onNext(): void
}

export interface HistoryTableProps {
  rows: RunSummary[]
  loading: boolean
  error: unknown
  paging: HistoryPaging
  onStartAgain(): void
  /** Mission 10 WP18 T104 (FR-019 10/9): a marker beside the state badge, from RunHistory. */
  note?: (run: RunSummary) => ReactNode
}

/** A cursor the API did not issue: 400 with `errors[].field === "cursor"`. */
export function isCursorRefusal(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.problem.status === 400 &&
    (error.problem.errors ?? []).some((e) => e.field === 'cursor')
  )
}

function runPath(run: RunSummary): string {
  return `/app/runs/${encodeURIComponent(run.run_id)}`
}

function When({ iso }: { iso: string }) {
  return (
    <time dateTime={isoDateTime(iso)}>
      {date(iso)} {time(iso)}
    </time>
  )
}

/** A private-use mark the transmission phrase is rendered around, so only its time sits in `<time>`. */
const TIME_MARK = '\uE001'

function Output({ run }: { run: RunSummary }) {
  if (run.state === 'rejected') {
    return (
      <Link to={runPath(run)} className="text-[var(--color-link)] underline-offset-4 hover:text-[var(--color-link-hover)] hover:underline">
        View errors
      </Link>
    )
  }
  const latest = run.latest_transmission ?? null
  const downloads =
    run.downloads.count > 0 ? (
      <span>
        Downloaded ×<Mono>{count(run.downloads.count)}</Mono>
        {run.downloads.last_at ? (
          <>
            {' · '}
            <When iso={run.downloads.last_at} />
          </>
        ) : null}
      </span>
    ) : null
  if (downloads === null && latest === null) return <>—</>
  return (
    <span className="flex flex-col gap-0.5">
      {downloads}
      {latest !== null ? (
        <Link
          to={`${runPath(run)}/transmissions`}
          className="text-[var(--color-link)] underline-offset-4 hover:text-[var(--color-link-hover)] hover:underline"
        >
          {/* Mission 7 review "Frontend residuals" (FR-022): <time> wraps the timestamp only, not the phrase. */}
          {PROVISIONAL_COPY.historyTransmission
            .render({ bureau: latest.bureau, result: latest.result, time: TIME_MARK })
            .split(TIME_MARK)
            .map((part, i) => (
              <Fragment key={i}>
                {i > 0 ? <When iso={latest.attempted_at} /> : null}
                {part}
              </Fragment>
            ))}
        </Link>
      ) : null}
    </span>
  )
}

function HistoryRow({ run, note }: { run: RunSummary; note?: HistoryTableProps['note'] }) {
  const navigate = useNavigate()
  const tone = runStateTone(run.state)
  function open(event: MouseEvent<HTMLTableRowElement>) {
    if ((event.target as Element).closest('a')) return
    navigate(runPath(run))
  }
  return (
    <TableRow onClick={open} className="cursor-pointer">
      <TableCell>
        <Link to={runPath(run)} title={run.run_id} className="text-[var(--color-link)] hover:text-[var(--color-link-hover)] hover:underline">
          <Mono>{shortId(run.run_id)}</Mono>
        </Link>
      </TableCell>
      <TableCell className="whitespace-nowrap text-body-sm">
        <When iso={run.started_at} />
      </TableCell>
      <TableCell className="whitespace-nowrap">{PATH_WORDS[run.path] ?? run.path}</TableCell>
      <TableCell className="max-w-[14rem]">
        {run.upload ? (
          <Mono title={run.upload.filename} className="block truncate text-body-sm">
            {run.upload.filename}
          </Mono>
        ) : null}
      </TableCell>
      <TableCell numeric>{count(run.record_count)}</TableCell>
      <TableCell>{run.rule_set_version === 'not-run' ? 'not run' : <Mono className="text-body-sm">{run.rule_set_version}</Mono>}</TableCell>
      <TableCell numeric data-severity-count="ERROR">
        {count(run.error_count)}
      </TableCell>
      <TableCell numeric data-severity-count="WARN">
        {count(run.warn_count)}
      </TableCell>
      <TableCell numeric data-severity-count="INFO">
        {count(run.info_count)}
      </TableCell>
      <TableCell numeric>{count(run.credits_consumed)}</TableCell>
      <TableCell>
        <span className="flex flex-wrap items-center gap-1.5">
          <Badge status={tone.status} dot={tone.dot} pulse={tone.dot} data-run-state={run.state}>
            {stateWord(run.state)}
          </Badge>
          {/* mission 7 WP14: J3, the badge text is a register entry (P22). */}
          {run.deliverable === false ? <Badge status="not-deliverable">{copyText('notDeliverableBadge')}</Badge> : null}
          {note?.(run)}
        </span>
      </TableCell>
      <TableCell className="whitespace-nowrap text-body-sm">
        <Output run={run} />
      </TableCell>
    </TableRow>
  )
}

function Footer({ paging, rows }: { paging: HistoryPaging; rows: number }) {
  return (
    <div data-history-footer="" className="mt-3 flex items-center justify-between text-caption text-navy-600">
      <Mono>{`${paging.offset + 1}–${paging.offset + rows}`}</Mono>
      {paging.hasPrevious || paging.hasNext ? (
        <div className="flex items-center gap-2">
          {paging.hasPrevious ? (
            <Button variant="secondary" size="sm" icon={<ChevronLeft aria-hidden />} onClick={paging.onPrevious}>
              Previous
            </Button>
          ) : null}
          {paging.hasNext ? (
            <Button variant="secondary" size="sm" onClick={paging.onNext}>
              Next
              <ChevronRight aria-hidden />
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

export function HistoryTable({ rows, loading, error, paging, onStartAgain, note }: HistoryTableProps) {
  if (error) {
    if (isCursorRefusal(error)) {
      return (
        <Band
          kind="info"
          action={
            <Button variant="secondary" size="sm" onClick={onStartAgain}>
              {/* owner decision 2026-09-26 (decision 9): was the inline "Start again". */}
              {copyText('backToFirstPage')}
            </Button>
          }
        >
          {copyText('genericError')}
        </Band>
      )
    }
    if (error instanceof ApiError) return <ProblemBand problem={error.problem} />
    return <Band kind="blocked">{copyText('genericError')}</Band>
  }
  return (
    <div>
      <Table>
        <TableCaption>{copyText('timesInUtc')}</TableCaption>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            {HEADERS.map((h) => (
              <TableHead key={h} className={h === 'Records' ? 'text-right' : undefined}>
                {h}
              </TableHead>
            ))}
            {SEVERITIES.map((s) => (
              <TableHead key={s} className="text-right">
                {s}
              </TableHead>
            ))}
            <TableHead className="text-right">Credits</TableHead>
            <TableHead>State</TableHead>
            <TableHead>Output</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            <TableSkeletonRows columns={COLUMNS} />
          ) : rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={COLUMNS} className="text-center text-body text-navy-600">
                {copyText('noRunsMatch')}
              </TableCell>
            </TableRow>
          ) : (
            rows.map((run) => <HistoryRow key={run.run_id} run={run} note={note} />)
          )}
        </TableBody>
      </Table>
      {!loading && rows.length > 0 ? <Footer paging={paging} rows={rows.length} /> : null}
    </div>
  )
}
